import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { authApi, propertyApi, invalidateAuthSession } from '../services/api';
import { createOAuthState } from '../utils/oauthState';

interface User {
  id: number;
  username: string;
  email: string;
  phone?: string;
}

interface KakaoUser {
  kakao_id: string;
  nickname: string | null;
  email: string | null;
  phone_number: string | null;
  profile_image_url: string | null;
}

interface AuthContextType {
  user: User | null;
  kakaoUser: KakaoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithKakao: () => Promise<void>;
  handleKakaoCallback: (code: string, consentMarketing?: boolean) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [kakaoUser, setKakaoUser] = useState<KakaoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authGeneration = useRef(0);
  const currentUserId = useRef(user?.id);
  const [pageGeneration, setPageGeneration] = useState(0);
  const setIdentity = (nextUser: User | null) => {
    // Keep OAuth callback mounted during guest → member login. Only discard
    // pages that could hold data from a previous member session.
    if (currentUserId.current !== undefined && currentUserId.current !== nextUser?.id) {
      setPageGeneration((generation) => generation + 1);
    }
    currentUserId.current = nextUser?.id;
    setUser(nextUser);
  };

  // 초기 로드 시 localStorage에서 사용자 정보 복원
  useEffect(() => {
    // Discard private payloads persisted by older versions.
    sessionStorage.removeItem('properties_cache');
    sessionStorage.removeItem('properties_cache_time');
    const initAuth = async () => {
      const generation = authGeneration.current;
      try {
        const storedUser = localStorage.getItem('user');
        const storedKakaoUser = localStorage.getItem('kakao_user');
        const accessToken = localStorage.getItem('access_token');

        if (storedUser && accessToken) {
          const parsedUser = JSON.parse(storedUser);
          setIdentity(parsedUser);

          if (storedKakaoUser) {
            setKakaoUser(JSON.parse(storedKakaoUser));
          }

          // 토큰 유효성 검증 (프로필 조회)
          try {
            const profile = await authApi.getProfile();
            if (generation === authGeneration.current && localStorage.getItem('access_token')) setIdentity(profile);
          } catch (error) {
            // 토큰 만료 시 로그아웃
            console.error('Token validation failed:', error);
            if (generation === authGeneration.current) logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    const clearIdentity = () => {
      authGeneration.current += 1;
      invalidateAuthSession();
      propertyApi.clearCache();
      setIdentity(null);
      setKakaoUser(null);
    };
    const syncStorage = (event: StorageEvent) => {
      if (event.key !== null && !['user', 'access_token', 'kakao_user'].includes(event.key)) return;
      // Read the current shared snapshot: an old queued logout event may arrive
      // after a new login. Observers must never delete another tab's credentials.
      const storedUser = localStorage.getItem('user');
      if (storedUser && localStorage.getItem('access_token')) {
        try {
          const nextUser = JSON.parse(storedUser);
          if (nextUser?.id == null) { clearIdentity(); return; }
          if (event.key === 'user' || nextUser.id !== currentUserId.current) {
            authGeneration.current += 1;
            invalidateAuthSession();
            propertyApi.clearCache();
          }
          setIdentity(nextUser);
          setKakaoUser(JSON.parse(localStorage.getItem('kakao_user') || 'null'));
        } catch { clearIdentity(); }
      } else {
        clearIdentity();
      }
    };
    window.addEventListener('storage', syncStorage);
    window.addEventListener('voonang:auth-cleared', clearIdentity);
    return () => {
      window.removeEventListener('storage', syncStorage);
      window.removeEventListener('voonang:auth-cleared', clearIdentity);
    };
  }, []);

  // 카카오 로그인 시작 (인증 페이지로 리다이렉트)
  const loginWithKakao = async () => {
    try {
      // 현재 페이지 URL 저장 (로그인 후 돌아갈 위치)
      // 로그인 화면(/login?reason=… 포함)에서 누른 경우엔 덮어쓰지 않는다 — 들어오기 전에 저장한
      // 돌아갈 곳(예: 상세 '혜택 보기' 버튼의 현장 주소)을 지켜야 한다 (2026-10-01)
      const { pathname } = window.location;
      const currentPath = pathname + window.location.search;

      if (pathname !== '/login' && pathname !== '/auth/kakao/callback') {
        localStorage.setItem('redirect_after_login', currentPath);
      }

      // 이 브라우저에 묶인 1회용 state (콜백에서 대조) — 2026-10-05
      const { auth_url } = await authApi.kakaoGetAuthUrl(createOAuthState());
      window.location.href = auth_url;
    } catch (error) {
      console.error('Failed to get Kakao auth URL:', error);
      throw error;
    }
  };

  // 카카오 콜백 처리
  const handleKakaoCallback = async (code: string, consentMarketing?: boolean) => {
    const generation = authGeneration.current;
    try {
      const response = await authApi.kakaoCallback(code, consentMarketing);
      if (generation !== authGeneration.current) return;
      authGeneration.current += 1;
      invalidateAuthSession();
      propertyApi.clearCache();

      // 토큰 및 사용자 정보 저장
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('kakao_user', JSON.stringify(response.kakao_user));

      // 동의 정보 정리 (로그인 완료 후)
      localStorage.removeItem('consent_privacy');
      localStorage.removeItem('consent_terms');
      localStorage.removeItem('consent_marketing');
      localStorage.removeItem('consent_date');

      setIdentity(response.user);
      setKakaoUser(response.kakao_user);
    } catch (error) {
      console.error('Kakao callback failed:', error);
      throw error;
    }
  };

  const logout = () => {
    authGeneration.current += 1;
    invalidateAuthSession();
    propertyApi.clearCache();
    // localStorage 정리
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('kakao_user');

    setIdentity(null);
    setKakaoUser(null);
  };

  const refreshToken = async (): Promise<boolean> => {
    const generation = authGeneration.current;
    try {
      const refresh = localStorage.getItem('refresh_token');

      if (!refresh) {
        return false;
      }

      const response = await authApi.refreshToken(refresh);
      if (generation !== authGeneration.current || localStorage.getItem('refresh_token') !== refresh) return false;
      localStorage.setItem('access_token', response.access);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (generation === authGeneration.current) logout();
      return false;
    }
  };

  // 사용자 정보 업데이트
  const updateUser = (updatedUser: User) => {
    setIdentity(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    kakaoUser,
    isAuthenticated: !!user,
    isLoading,
    loginWithKakao,
    handleKakaoCallback,
    logout,
    refreshToken,
    updateUser,
  };

  // Destroy mounted page state on logout/account switch, not only the auth badge.
  // Router lives outside this provider, so the current URL is preserved.
  return <AuthContext.Provider value={value}><React.Fragment key={pageGeneration}>{children}</React.Fragment></AuthContext.Provider>;
};
